/**
 * V280.0: Global Upload Guard
 * 
 * CRITICAL: This guard MUST be called before ANY Cloudinary upload to prevent:
 * 1. Placeholder images (SVG, data URLs, small files) from being uploaded
 * 2. Display names being used as public_id instead of userId
 * 3. Invalid or missing user IDs
 * 
 * This is the DEFINITIVE fix for the "BlissNet_App" placeholder upload issue.
 */

/**
 * Custom error class for blocked uploads
 */
export class UploadBlockedError extends Error {
  constructor(reason: string, details?: any) {
    super(`V280.0: Upload blocked - ${reason}`);
    this.name = 'UploadBlockedError';
    
    // Log the block with details
    console.error('🚫 V280.0: UPLOAD BLOCKED!');
    console.error('🚫 V280.0: Reason:', reason);
    if (details) {
      console.error('🚫 V280.0: Details:', details);
    }
    console.error('🚫 V280.0: Stack trace:', new Error().stack);
  }
}

/**
 * Validate that a source is safe to upload to Cloudinary
 * 
 * @param source - The image source (File, Blob, or string URL)
 * @param userId - The user ID (must be a valid UUID)
 * @param context - Where this upload is coming from (for debugging)
 * @throws UploadBlockedError if validation fails
 */
export function validateUpload(
  source: string | File | Blob | null | undefined,
  userId: string,
  context: string = 'unknown'
): void {
  console.log(`🔍 V280.0: Validating upload from ${context}`);
  console.log(`🔍 V280.0: Source type:`, typeof source);
  console.log(`🔍 V280.0: User ID:`, userId);
  
  // Block if no source
  if (!source) {
    throw new UploadBlockedError('No source provided', { context });
  }
  
  // Block if no user ID
  if (!userId || typeof userId !== 'string') {
    throw new UploadBlockedError('Invalid user ID (missing or not a string)', { 
      context, 
      userId: userId?.toString?.() || 'undefined' 
    });
  }
  
  // Validate user ID is a UUID (36 characters with hyphens)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(userId)) {
    throw new UploadBlockedError('Invalid user ID format (not a UUID)', { 
      context, 
      userId,
      length: userId.length 
    });
  }
  
  // Block data URLs (these are placeholders or client-side generated images)
  if (typeof source === 'string') {
    if (source.startsWith('data:')) {
      throw new UploadBlockedError('Data URLs are blocked (client-side placeholder)', { 
        context,
        sourcePrefix: source.substring(0, 50) 
      });
    }
    if (source.toLowerCase().includes('svg')) {
      throw new UploadBlockedError('SVG content is blocked (placeholder)', { 
        context,
        sourcePrefix: source.substring(0, 50) 
      });
    }
    if (source.includes('placeholder') || source.includes('fallback')) {
      throw new UploadBlockedError('Placeholder URLs are blocked', { 
        context,
        sourcePrefix: source.substring(0, 50) 
      });
    }
    if (source.startsWith('blob:')) {
      // Blob URLs are OK for upload (they're real images in memory)
      console.log('✅ V280.0: Blob URL detected (OK for upload)');
    }
  }
  
  // Block small files (< 5KB is definitely a placeholder)
  if (source instanceof Blob) {
    console.log(`🔍 V280.0: Blob size: ${source.size} bytes (${Math.round(source.size / 1024)}KB)`);
    
    if (source.size < 5000) {
      throw new UploadBlockedError(`File too small (${source.size} bytes < 5KB) - likely a placeholder`, { 
        context,
        size: source.size,
        sizeKB: Math.round(source.size / 1024)
      });
    }
    
    if (source.type?.includes('svg')) {
      throw new UploadBlockedError('SVG files are blocked (placeholder)', { 
        context,
        type: source.type 
      });
    }
    
    // Validate it's an image
    if (source.type && !source.type.startsWith('image/')) {
      throw new UploadBlockedError('Not an image file', { 
        context,
        type: source.type 
      });
    }
  }
  
  console.log(`✅ V280.0: Upload validated from ${context}`);
  console.log(`✅ V280.0: Source size: ${source instanceof Blob ? Math.round(source.size / 1024) + 'KB' : 'N/A'}`);
  console.log(`✅ V280.0: User ID: ${userId.substring(0, 8)}...`);
}

/**
 * Sanitize and validate public_id to ensure it ALWAYS uses userId
 * 
 * CRITICAL: This prevents display names from being used as public_id
 * 
 * @param userId - The user ID (must be a valid UUID)
 * @param folder - The Cloudinary folder (default: 'profile-photos')
 * @returns The sanitized public_id (just the userId, folder is handled separately)
 * @throws UploadBlockedError if userId is invalid
 */
export function sanitizePublicId(userId: string, folder: string = 'profile-photos'): string {
  console.log(`🔍 V280.0: Sanitizing public_id`);
  console.log(`🔍 V280.0: Input userId:`, userId);
  console.log(`🔍 V280.0: Folder:`, folder);
  
  // Validate user ID
  if (!userId || typeof userId !== 'string') {
    throw new UploadBlockedError('Cannot create public_id: Invalid user ID', { userId });
  }
  
  // Validate it's a UUID (NOT a display name!)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(userId)) {
    throw new UploadBlockedError(
      'Cannot create public_id: userId is not a UUID (might be a display name!)', 
      { 
        userId,
        length: userId.length,
        warning: 'This might be a display name instead of a user ID!'
      }
    );
  }
  
  // Return JUST the userId (folder is handled separately in Cloudinary params)
  const publicId = userId;
  
  console.log(`✅ V280.0: Public ID sanitized:`, publicId);
  console.log(`✅ V280.0: Full path will be: blissnet/${folder}/${publicId}`);
  
  return publicId;
}

/**
 * Check if a URL is a real Cloudinary photo (not a placeholder)
 * 
 * @param url - The URL to check
 * @returns true if it's a real Cloudinary photo, false otherwise
 */
export function isRealCloudinaryPhoto(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }
  
  // Must be a Cloudinary URL
  if (!url.includes('res.cloudinary.com')) {
    return false;
  }
  
  // Must not be a placeholder
  if (url.includes('placeholder') || url.includes('fallback')) {
    return false;
  }
  
  // Must be in the profile-photos folder
  if (!url.includes('blissnet/profile-photos/')) {
    return false;
  }
  
  // Must have a UUID in the path (not a display name!)
  const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
  if (!uuidRegex.test(url)) {
    console.warn('⚠️ V280.0: Cloudinary URL does not contain a UUID:', url.substring(0, 100));
    return false;
  }
  
  return true;
}

/**
 * Log upload attempt with full context (for debugging)
 * 
 * @param source - The image source
 * @param userId - The user ID
 * @param context - Where this upload is coming from
 */
export function logUploadAttempt(
  source: string | File | Blob | null | undefined,
  userId: string,
  context: string
): void {
  console.log('═══════════════════════════════════════════════════════');
  console.log('📤 V280.0: CLOUDINARY UPLOAD ATTEMPT');
  console.log('═══════════════════════════════════════════════════════');
  console.log('📍 Context:', context);
  console.log('📍 User ID:', userId);
  console.log('📍 Source type:', typeof source);
  
  if (source instanceof Blob) {
    console.log('📍 Source size:', source.size, 'bytes (', Math.round(source.size / 1024), 'KB)');
    console.log('📍 Source mime:', source.type);
  } else if (typeof source === 'string') {
    console.log('📍 Source URL prefix:', source.substring(0, 100));
  }
  
  console.log('📍 Stack trace:');
  console.log(new Error().stack);
  console.log('═══════════════════════════════════════════════════════');
}

